import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useCart } from '../contexts/CartContext';
import { useSavedProducts } from '../contexts/SavedProductsContext';
import { formatTimeLocale } from '../utils/i18nHelpers';
import { api, API_URL, resolveMediaUrl } from '../utils/api';
import Skeleton from '../components/Skeleton';
import { UserAnalyticsPanel } from './UserAnalytics';


const STATUS_COLORS = {
  confirmed: { bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.3)', text: '#4ade80' },
  pending: { bg: 'var(--card-hover-ring)', border: 'var(--border-hover)', text: 'var(--orange)' },
  cancelled: { bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.3)', text: '#f87171' },
};

function ProfileContent() {
  const { t, i18n } = useTranslation();
  const { showAlert } = useNotification();
  const { user, setUser } = useAuth();
  const { addToCart } = useCart();
  const { wishlist, loadingSaved, toggleWishlist, isWishlisted } = useSavedProducts();
  const locale = i18n.language === 'ar' ? 'ar-EG' : 'en-GB';
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [ratingBookingId, setRatingBookingId] = useState(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editProfileForm, setEditProfileForm] = useState({ name: '', email: '', phone_number: '', password: '' });
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [memberships, setMemberships] = useState([]);
  const [activeTab, setActiveTab] = useState('profile');

  const fetchBookings = () => {
    setLoading(true);
    api.getBookings()
      .then(setBookings)
      .catch(err => console.error('Failed to fetch bookings:', err))
      .finally(() => setLoading(false));
  };

  const handleRateBooking = async (bookingId, rating) => {
    try {
      setRatingBookingId(bookingId);
      await api.rateBooking(bookingId, rating);
      fetchBookings(); // Refresh to get updated rating and backend calc
    } catch (err) {
      console.error('Failed to rate booking:', err);
      showAlert('Failed to submit rating. Please try again.');
    } finally {
      setRatingBookingId(null);
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const updatedUser = await api.uploadProfilePicture(formData);
      setUser(updatedUser);
    } catch (error) {
      console.error('Failed to upload profile picture:', error);
      showAlert('Failed to upload profile picture. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = null; // reset input
    }
  };

  const handleEditProfileOpen = () => {
    setEditProfileForm({
      name: user?.name || '',
      email: user?.email || '',
      phone_number: user?.phone_number || '',
      password: ''
    });
    setIsEditingProfile(true);
  };

  const handleEditProfileSubmit = async (e) => {
    e.preventDefault();
    setUpdatingProfile(true);
    try {
      const dataToUpdate = {};
      if (editProfileForm.name !== user?.name) dataToUpdate.name = editProfileForm.name;
      if (editProfileForm.email !== user?.email) dataToUpdate.email = editProfileForm.email;
      if (editProfileForm.phone_number !== user?.phone_number) dataToUpdate.phone_number = editProfileForm.phone_number;
      if (editProfileForm.password) dataToUpdate.password = editProfileForm.password;

      if (Object.keys(dataToUpdate).length > 0) {
        const updatedUser = await api.updateProfile(dataToUpdate);
        setUser(updatedUser);
        showAlert(t('profile.profileUpdated', 'Profile updated successfully'));
      }
      setIsEditingProfile(false);
    } catch (err) {
      console.error('Failed to update profile:', err);
      showAlert(err.message || t('profile.updateFailed', 'Failed to update profile'));
    } finally {
      setUpdatingProfile(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    api.getMyMemberships().then(setMemberships).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      api.getMe()
        .then(data => { if (data) setUser(data); })
        .catch(() => { });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)', color: '#ffffff', padding: '7rem 1.5rem 5rem', fontFamily: "var(--font-body)" }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '3rem' }}>
          <p style={{ fontFamily: "var(--font-heading)", fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '0.5rem' }}>{t('profile.yourAccount')}</p>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: 'clamp(2.5rem, 6vw, 4rem)', letterSpacing: '0.06em', color: '#ffffff', lineHeight: 1, marginBottom: '0.25rem' }}>{t('profile.myProfile')}</h1>
          <div style={{ width: '60px', height: '4px', background: 'var(--orange)', borderRadius: '2px' }} />
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setActiveTab('profile')}
            style={{
              border: activeTab === 'profile' ? '1px solid var(--orange)' : '1px solid #333333',
              background: activeTab === 'profile' ? 'rgba(255,255,255,0.08)' : 'transparent',
              color: '#ffffff',
              borderRadius: '8px',
              padding: '0.55rem 1rem',
              fontFamily: "var(--font-heading)",
              fontSize: '0.8rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            {t('profile.myProfile')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('analytics')}
            style={{
              border: activeTab === 'analytics' ? '1px solid var(--orange)' : '1px solid #333333',
              background: activeTab === 'analytics' ? 'rgba(255,255,255,0.08)' : 'transparent',
              color: '#ffffff',
              borderRadius: '8px',
              padding: '0.55rem 1rem',
              fontFamily: "var(--font-heading)",
              fontSize: '0.8rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            {t('analytics.myAnalytics')}
          </button>
        </div>

        {activeTab === 'profile' && (
          <>
        {/* User Info Card */}
        <div style={{ background: '#1a1a1a', borderRadius: '12px', padding: 'clamp(1.5rem, 4vw, 2rem)', marginBottom: '2.5rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1.5rem' }}>
          {/* Avatar */}
          <label style={{ position: 'relative', cursor: 'pointer', flexShrink: 0, display: 'inline-block' }}
            onMouseEnter={(e) => e.currentTarget.querySelector('.photo-overlay').style.opacity = 1}
            onMouseLeave={(e) => e.currentTarget.querySelector('.photo-overlay').style.opacity = 0}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'var(--orange)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "var(--font-display)", fontSize: '2.5rem', color: '#fff', overflow: 'hidden' }}>
              {user?.profile_picture_url ? (
                <img src={resolveMediaUrl(user.profile_picture_url)} alt="Profile" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                user?.name?.[0]?.toUpperCase() || '?'
              )}
            </div>

            <div className="photo-overlay" style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)', borderRadius: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s', color: '#fff', fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
              📷<br />{t('profile.addPhoto')}
            </div>

            {uploading && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '0.8rem', color: '#ffffff' }}>⏳</span>
              </div>
            )}
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleProfilePictureUpload} disabled={uploading} />
          </label>
          
          {/* Details */}
          <div style={{ flex: '1 1 200px' }}>
            <h2 style={{ fontFamily: "var(--font-heading)", fontSize: '1.5rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '0.2rem' }}>{user?.name}</h2>
            <p style={{ fontSize: '0.85rem', color: '#ffffff', marginBottom: '0.4rem' }}>{user?.email}</p>
            <span style={{ display: 'inline-block', padding: '0.25rem 0.5rem', borderRadius: '4px', background: '#333333', color: '#ffffff', fontSize: '0.65rem', fontWeight: 700, fontFamily: "var(--font-heading)", letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {user?.role || 'Customer'}
            </span>
          </div>

          {/* Edit Button */}
          <div style={{ flex: '1 1 100%', display: 'flex', justifyContent: 'flex-start' }}>
            <button
              onClick={handleEditProfileOpen}
              style={{
                width: '100%',
                maxWidth: '200px',
                padding: '0.75rem 1rem',
                background: 'var(--orange)',
                border: 'none',
                borderRadius: '6px',
                color: '#ffffff',
                fontFamily: "var(--font-heading)",
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: 'pointer',
                letterSpacing: '0.05em',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = 'var(--orange-dark)'}
              onMouseLeave={(e) => e.target.style.background = 'var(--orange)'}
            >
              EDIT PROFILE
            </button>
          </div>
        </div>

        {/* Edit Profile Form */}
        {isEditingProfile && (
          <div style={{ background: '#1a1a1a', borderRadius: '12px', padding: 'clamp(1.5rem, 4vw, 2rem)', marginBottom: '2.5rem' }}>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: '1.8rem', color: '#fff', marginBottom: '1.5rem', letterSpacing: '0.05em' }}>Edit Details</h2>
            <form onSubmit={handleEditProfileSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#ffffff', marginBottom: '0.4rem', textTransform: 'uppercase', fontFamily: "var(--font-heading)" }}>Name</label>
                  <input type="text" value={editProfileForm.name} onChange={e => setEditProfileForm({ ...editProfileForm, name: e.target.value })} className="barber-input" required />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#ffffff', marginBottom: '0.4rem', textTransform: 'uppercase', fontFamily: "var(--font-heading)" }}>Phone Number</label>
                  <input type="tel" value={editProfileForm.phone_number} onChange={e => setEditProfileForm({ ...editProfileForm, phone_number: e.target.value })} className="barber-input" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#ffffff', marginBottom: '0.4rem', textTransform: 'uppercase', fontFamily: "var(--font-heading)" }}>Email</label>
                  <input type="email" value={editProfileForm.email} onChange={e => setEditProfileForm({ ...editProfileForm, email: e.target.value })} className="barber-input" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#ffffff', marginBottom: '0.4rem', textTransform: 'uppercase', fontFamily: "var(--font-heading)" }}>New Password <span style={{ textTransform: 'none', color: '#777' }}>(leave blank to keep current)</span></label>
                  <input type="password" value={editProfileForm.password} onChange={e => setEditProfileForm({ ...editProfileForm, password: e.target.value })} className="barber-input" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button type="button" onClick={() => setIsEditingProfile(false)} style={{ padding: '0.75rem 1.5rem', background: 'transparent', border: '1px solid #333', borderRadius: '6px', color: '#ffffff', cursor: 'pointer', fontFamily: "var(--font-heading)" }}>Cancel</button>
                <button type="submit" disabled={updatingProfile} style={{ padding: '0.75rem 1.5rem', background: 'var(--orange)', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', fontFamily: "var(--font-heading)", opacity: updatingProfile ? 0.7 : 1 }}>
                  {updatingProfile ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* VIP Memberships */}
        {memberships.length > 0 && (
          <div style={{ marginBottom: '3rem' }}>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: '1.8rem', letterSpacing: '0.06em', color: '#ffffff', marginBottom: '1rem' }}>VIP Memberships</h2>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {memberships.map((m, idx) => {
                const statusColorMap = { active: '#4ade80', pending_approval: '#FFD700', expired: '#f87171' };
                const statusBgMap = { active: 'rgba(34,197,94,0.1)', pending_approval: 'rgba(255,215,0,0.1)', expired: 'rgba(239,68,68,0.1)' };
                const status = m.status || 'pending_approval';
                return (
                  <div key={m.id || idx} style={{ background: '#1a1a1a', borderRadius: '12px', padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap-reverse', gap: '1rem' }}>
                    <div style={{ flex: '1 1 250px' }}>
                      <h3 style={{ fontFamily: "var(--font-heading)", fontSize: '1.1rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '0.2rem' }}>{m.shop_name}</h3>
                      {status === 'active' && m.subscription_start && m.subscription_end && (
                        <p style={{ fontSize: '0.85rem', color: '#ffffff', marginBottom: '0.8rem' }}>
                          {m.subscription_start} &mdash; {m.subscription_end}
                        </p>
                      )}
                      <Link to="/premium" style={{ display: 'inline-block', padding: '0.5rem 1rem', borderRadius: '6px', background: 'transparent', border: '1px solid #ffffff', color: '#ffffff', fontFamily: "var(--font-heading)", fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.05em', textDecoration: 'none', transition: 'all 0.2s ease' }}
                        onMouseEnter={(e) => { e.target.style.background = '#ffffff'; e.target.style.color = '#000000'; }}
                        onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.color = '#ffffff'; }}>
                        Go to Premium
                      </Link>
                    </div>
                    <span style={{ padding: '0.4rem 0.8rem', borderRadius: '4px', background: statusBgMap[status] || statusBgMap.pending_approval, color: statusColorMap[status] || statusColorMap.pending_approval, fontSize: '0.75rem', fontFamily: "var(--font-heading)", fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', flexShrink: 0 }}>
                      {status.replace('_', ' ')}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Wishlist */}
        <div style={{ marginBottom: '3rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h2 style={{ fontFamily: "var(--font-display)", fontSize: '1.8rem', letterSpacing: '0.06em', color: '#ffffff', marginBottom: '0.2rem' }}>
                Wishlist
              </h2>
              <p style={{ fontSize: '0.85rem', color: '#ffffff' }}>Saved products you want to buy later.</p>
            </div>
          </div>

          {loadingSaved ? (
            <Skeleton count={2} height="180px" borderRadius="12px" />
          ) : wishlist.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 2rem', border: '1px dashed #333', borderRadius: '12px', background: '#1a1a1a' }}>
              <p style={{ fontFamily: "var(--font-heading)", fontSize: '1rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '0.5rem' }}>
                Your wishlist is empty
              </p>
              <p style={{ fontSize: '0.88rem', color: '#ffffff', marginBottom: '1.25rem' }}>
                Browse products and tap the heart to save items here.
              </p>
              <Link to="/products" className="btn-orange" style={{ fontSize: '0.85rem' }}>Browse Products</Link>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {wishlist.map((item) => {
                const product = item.product;
                if (!product) {
                  return (
                    <div key={item.id} style={{ background: '#1a1a1a', borderRadius: '12px', padding: '1rem', color: '#ffffff' }}>
                      Product is unavailable.
                    </div>
                  );
                }
                return (
                  <div key={item.id} style={{ background: '#1a1a1a', borderRadius: '12px', padding: '1.25rem', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Link to={`/products/${product.id}`} style={{ width: '88px', height: '88px', borderRadius: '8px', overflow: 'hidden', background: '#121212', border: '1px solid #333', flexShrink: 0 }}>
                      {product.photo_url ? (
                        <img src={resolveMediaUrl(product.photo_url)} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🧴</div>
                      )}
                    </Link>

                    <div style={{ flex: '1 1 240px' }}>
                      <Link to={`/products/${product.id}`} style={{ color: '#ffffff', textDecoration: 'none' }}>
                        <h3 style={{ fontFamily: "var(--font-heading)", fontSize: '1rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                          {product.name}
                        </h3>
                      </Link>
                      <p style={{ fontSize: '0.8rem', color: '#ffffff', marginBottom: '0.35rem' }}>{product.category || 'General'}</p>
                      <p style={{ fontFamily: "var(--font-body)", fontSize: '1rem', fontWeight: 700, color: '#ffffff' }}>{product.price} EGP</p>
                    </div>

                    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => {
                          addToCart(product);
                          showAlert('Added to cart');
                        }}
                        style={{ padding: '0.55rem 0.85rem', background: 'var(--orange)', border: 'none', borderRadius: '6px', color: '#fff', fontFamily: "var(--font-heading)", fontSize: '0.78rem', cursor: 'pointer', letterSpacing: '0.04em', textTransform: 'uppercase' }}
                      >
                        Add to cart
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const added = await toggleWishlist(product.id);
                            showAlert(added ? 'Added to wishlist' : 'Removed from wishlist');
                          } catch (err) {
                            showAlert(err.message || 'Wishlist action failed');
                          }
                        }}
                        style={{ padding: '0.55rem 0.85rem', background: 'transparent', border: '1px solid #444', borderRadius: '6px', color: isWishlisted(product.id) ? 'var(--orange)' : '#ffffff', fontFamily: "var(--font-heading)", fontSize: '0.78rem', cursor: 'pointer', letterSpacing: '0.04em', textTransform: 'uppercase' }}
                      >
                        {isWishlisted(product.id) ? '♥ Saved' : '♡ Save'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Bookings */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h2 style={{ fontFamily: "var(--font-display)", fontSize: '1.8rem', letterSpacing: '0.06em', color: '#ffffff', marginBottom: '0.2rem' }}>{t('profile.myBookingsTitle')}</h2>
              <p style={{ fontSize: '0.85rem', color: '#ffffff' }}>{t('profile.noCancellationPolicy')}</p>
            </div>
          </div>

          {loading ? (
            <Skeleton count={3} height="120px" borderRadius="12px" />
          ) : bookings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem 2rem', border: '1px dashed #333', borderRadius: '12px', background: '#1a1a1a' }}>
              <p style={{ fontFamily: "var(--font-heading)", fontSize: '1.1rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '0.5rem' }}>{t('profile.noBookingsYet')}</p>
              <p style={{ fontSize: '0.88rem', color: '#ffffff', marginBottom: '1.5rem' }}>{t('profile.noBookingsDescription')}</p>
              <Link to="/shops" className="btn-orange" style={{ fontSize: '0.85rem' }}>{t('profile.browseShops')}</Link>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {bookings.map(booking => {
                const statusStyle = STATUS_COLORS[booking.status?.toLowerCase()] || STATUS_COLORS.pending;
                return (
                  <div key={booking.id} style={{ background: '#1a1a1a', borderRadius: '12px', padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap-reverse', gap: '1rem' }}>
                    <div style={{ flex: '1 1 250px' }}>
                      <h3 style={{ fontFamily: "var(--font-heading)", fontSize: '1.2rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '0.2rem' }}>{booking.service_name}</h3>
                      <p style={{ fontSize: '0.85rem', color: '#ffffff', marginBottom: '0.4rem' }}>📅 {booking.booking_date} at {formatTimeLocale(booking.booking_time, locale)}</p>
                      <p style={{ fontFamily: "var(--font-body)", fontSize: '1.1rem', fontWeight: 700, color: '#ffffff', marginBottom: '1rem' }}>{booking.service_price} EGP</p>

                      {(booking.status === 'completed' || booking.status === 'confirmed') && (
                        <div style={{ paddingTop: '1rem', borderTop: '1px solid #333' }}>
                          {booking.rating ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{ fontSize: '0.8rem', color: '#fff', opacity: 0.7 }}>{t('profile.yourRating')}</span>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                {[1, 2, 3, 4, 5].map(star => (
                                  <span key={star} style={{ color: star <= booking.rating ? '#FFD700' : 'rgba(255,255,255,0.2)', fontSize: '1.2rem' }}>
                                    ★
                                  </span>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '0.8rem', color: '#ffffff', fontFamily: "var(--font-heading)", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {ratingBookingId === booking.id ? 'Saving...' : 'Rate your visit:'}
                              </span>
                              {!ratingBookingId && (
                                <div style={{ display: 'flex', gap: '4px', cursor: 'pointer' }}>
                                  {[1, 2, 3, 4, 5].map(star => (
                                    <span
                                      key={star}
                                      onClick={() => handleRateBooking(booking.id, star)}
                                      onMouseEnter={(e) => {
                                        const stars = e.currentTarget.parentElement.children;
                                        for (let i = 0; i < stars.length; i++) {
                                          stars[i].style.color = i < star ? '#FFD700' : 'rgba(255,255,255,0.2)';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        const stars = e.currentTarget.parentElement.children;
                                        for (let i = 0; i < stars.length; i++) {
                                          stars[i].style.color = 'rgba(255,255,255,0.2)';
                                        }
                                      }}
                                      style={{ color: 'rgba(255,255,255,0.2)', fontSize: '1.4rem', transition: 'color 0.1s' }}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <span style={{ padding: '0.4rem 0.8rem', borderRadius: '4px', background: statusStyle.bg, color: statusStyle.text, fontSize: '0.75rem', fontFamily: "var(--font-heading)", fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', flexShrink: 0 }}>
                      {booking.status}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
          </>
        )}

        {activeTab === 'analytics' && (
          <div style={{ background: '#1a1a1a', borderRadius: '12px', padding: '0.5rem' }}>
            <UserAnalyticsPanel embedded />
          </div>
        )}
      </div>
    </div>
  );
}

export default function Profile() {
  return (
    <ProfileContent />
  );
}
